/**
 * Centralized API Endpoints Configuration
 * 
 * This file contains all API endpoint definitions for the HomeIQ application.
 * All endpoints are prefixed with /api/v1 in the backend.
 * 
 * Usage:
 * import { API_ENDPOINTS } from '@/config/api-endpoints';
 * apiFetch(API_ENDPOINTS.AUTH.REGISTER, { method: 'POST', body: data });
 */

const BASE_PATH = '/v1';

export const API_ENDPOINTS = {
  /**
   * Authentication & Authorization
   */
  AUTH: {
    // POST /api/v1/auth/register
    REGISTER: `${BASE_PATH}/auth/register`,
    
    // POST /api/v1/auth/refresh
    REFRESH: `${BASE_PATH}/auth/refresh`,
    
    // POST /api/v1/auth/logout
    LOGOUT: `${BASE_PATH}/auth/logout`,
    
    // POST /api/v1/auth/verify-email
    VERIFY_EMAIL: `${BASE_PATH}/auth/verify-email`,
    
    // POST /api/v1/auth/resend-otp
    RESEND_OTP: `${BASE_PATH}/auth/resend-otp`,
    
    // GET /api/v1/auth/google/login - Initiate Google OAuth
    GOOGLE_LOGIN: `${BASE_PATH}/auth/google/login`,
    
    // GET /api/v1/auth/google/callback - Google OAuth callback
    GOOGLE_CALLBACK: `${BASE_PATH}/auth/google/callback`,
  },

  /**
   * User Management
   */
  USERS: {
    // GET /api/v1/users/me
    GET_ME: `${BASE_PATH}/users/me`,
    
    // PUT /api/v1/users/me
    UPDATE_ME: `${BASE_PATH}/users/me`,

    // DELETE /api/v1/users/me
    DELETE_ME: `${BASE_PATH}/users/me`,
    
    // POST /api/v1/users/device-token
    REGISTER_DEVICE_TOKEN: `${BASE_PATH}/users/device-token`,
    
    // DELETE /api/v1/users/device-token/:token
    REMOVE_DEVICE_TOKEN: (token: string) => `${BASE_PATH}/users/device-token/${token}`,

    // GET /api/v1/users/me/preferences
    GET_PREFERENCES: `${BASE_PATH}/users/me/preferences`,

    // PUT /api/v1/users/me/preferences
    UPDATE_PREFERENCES: `${BASE_PATH}/users/me/preferences`,

    // POST /api/v1/users/me/signout-all
    SIGNOUT_ALL: `${BASE_PATH}/users/me/signout-all`,

    // POST /api/v1/users/me/phone/request-change
    PHONE_REQUEST_CHANGE: `${BASE_PATH}/users/me/phone/request-change`,

    // POST /api/v1/users/me/phone/verify-change
    PHONE_VERIFY_CHANGE: `${BASE_PATH}/users/me/phone/verify-change`,
  },

  /**
   * Tenant Management
   */
  TENANTS: {
    // GET /api/v1/tenants/current
    GET_CURRENT: `${BASE_PATH}/tenants/current`,
    
    // GET /api/v1/tenants
    GET_ALL: `${BASE_PATH}/tenants`,
    
    // GET /api/v1/tenants/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/tenants/${id}`,
    
    // POST /api/v1/tenants
    CREATE: `${BASE_PATH}/tenants`,
    
    // PATCH /api/v1/tenants/:id
    UPDATE: (id: string) => `${BASE_PATH}/tenants/${id}`,
    
    // PATCH /api/v1/tenants/settings/:id
    UPDATE_SETTINGS: (id: string) => `${BASE_PATH}/tenants/settings/${id}`,
    
    // DELETE /api/v1/tenants/:id
    DELETE: (id: string) => `${BASE_PATH}/tenants/${id}`,
  },

  /**
   * Homes Management
   */
  HOMES: {
    // GET /api/v1/homes
    GET_ALL: `${BASE_PATH}/homes`,
    
    // POST /api/v1/homes
    CREATE: `${BASE_PATH}/homes`,
    
    // GET /api/v1/homes/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/homes/${id}`,
    
    // PUT /api/v1/homes/:id
    UPDATE: (id: string) => `${BASE_PATH}/homes/${id}`,
    
    // DELETE /api/v1/homes/:id
    DELETE: (id: string) => `${BASE_PATH}/homes/${id}`,
  },

  /**
   * Assets Management
   */
  ASSETS: {
    // GET /api/v1/assets?homeId=
    GET_ALL: `${BASE_PATH}/assets`,
    
    // POST /api/v1/assets
    CREATE: `${BASE_PATH}/assets`,
    
    // GET /api/v1/assets/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/assets/${id}`,
    
    // PUT /api/v1/assets/:id
    UPDATE: (id: string) => `${BASE_PATH}/assets/${id}`,
    
    // DELETE /api/v1/assets/:id
    DELETE: (id: string) => `${BASE_PATH}/assets/${id}`,
    
    // GET /api/v1/assets/:id/service-history
    GET_SERVICE_HISTORY: (id: string) => `${BASE_PATH}/assets/${id}/service-history`,
    
    // POST /api/v1/assets/:id/service-history
    ADD_SERVICE_RECORD: (id: string) => `${BASE_PATH}/assets/${id}/service-history`,
    
    // GET /api/v1/assets/:id/documents
    GET_DOCUMENTS: (id: string) => `${BASE_PATH}/assets/${id}/documents`,
    
    // POST /api/v1/assets/:id/documents/upload - multipart file upload
    UPLOAD_DOCUMENT: (id: string) => `${BASE_PATH}/assets/${id}/documents/upload`,
    
    // POST /api/v1/assets/:id/documents
    CREATE_DOCUMENT: (id: string) => `${BASE_PATH}/assets/${id}/documents`,
    
    // PATCH /api/v1/assets/:id/documents/:docId
    UPDATE_DOCUMENT: (assetId: string, docId: string) => 
      `${BASE_PATH}/assets/${assetId}/documents/${docId}`,
    
    // DELETE /api/v1/assets/:id/documents/:docId
    DELETE_DOCUMENT: (assetId: string, docId: string) => 
      `${BASE_PATH}/assets/${assetId}/documents/${docId}`,
  },

  /**
   * Issues Tracking
   */
  ISSUES: {
    // GET /api/v1/issues?homeId=&status=
    GET_ALL: `${BASE_PATH}/issues`,
    
    // POST /api/v1/issues
    CREATE: `${BASE_PATH}/issues`,
    
    // GET /api/v1/issues/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/issues/${id}`,
    
    // PUT /api/v1/issues/:id/status
    UPDATE_STATUS: (id: string) => `${BASE_PATH}/issues/${id}/status`,
    
    // DELETE /api/v1/issues/:id
    DELETE: (id: string) => `${BASE_PATH}/issues/${id}`,
  },

  /**
   * Maintenance Records
   */
  MAINTENANCE: {
    // GET /api/v1/maintenance?homeId=
    GET_ALL: `${BASE_PATH}/maintenance`,
    
    // POST /api/v1/maintenance
    CREATE: `${BASE_PATH}/maintenance`,
    
    // GET /api/v1/maintenance/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/maintenance/${id}`,
    
    // PUT /api/v1/maintenance/:id
    UPDATE: (id: string) => `${BASE_PATH}/maintenance/${id}`,
    
    // DELETE /api/v1/maintenance/:id
    DELETE: (id: string) => `${BASE_PATH}/maintenance/${id}`,
  },

  /**
   * Bookings
   */
  BOOKINGS: {
    // GET /api/v1/bookings
    GET_ALL: `${BASE_PATH}/bookings`,
    
    // POST /api/v1/bookings
    CREATE: `${BASE_PATH}/bookings`,
    
    // GET /api/v1/bookings/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/bookings/${id}`,
    
    // PUT /api/v1/bookings/:id/cancel
    CANCEL: (id: string) => `${BASE_PATH}/bookings/${id}/cancel`,
    
    // PUT /api/v1/bookings/:id/reschedule
    RESCHEDULE: (id: string) => `${BASE_PATH}/bookings/${id}/reschedule`,
    
    // POST /api/v1/bookings/:id/rate
    RATE: (id: string) => `${BASE_PATH}/bookings/${id}/rate`,
  },

  /**
   * Service Bookings
   */
  SERVICE_BOOKINGS: {
    // GET /api/v1/service-bookings
    GET_ALL: `${BASE_PATH}/service-bookings`,
    
    // POST /api/v1/service-bookings
    CREATE: `${BASE_PATH}/service-bookings`,
    
    // GET /api/v1/service-bookings/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/service-bookings/${id}`,
    
    // PUT /api/v1/service-bookings/:id
    UPDATE: (id: string) => `${BASE_PATH}/service-bookings/${id}`,
  },

  /**
   * Warranty Management
   */
  WARRANTY: {
    // GET /api/v1/warranty?homeId=
    GET_ALL: `${BASE_PATH}/warranty`,
    
    // GET /api/v1/warranty/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/warranty/${id}`,
    
    // POST /api/v1/warranty/:id/claim
    FILE_CLAIM: (id: string) => `${BASE_PATH}/warranty/${id}/claim`,
    
    // GET /api/v1/warranty/:id/claims
    GET_CLAIMS: (id: string) => `${BASE_PATH}/warranty/${id}/claims`,
    
    // GET /api/v1/warranty/claims/all
    GET_ALL_USER_CLAIMS: `${BASE_PATH}/warranty/claims/all`,
    
    // PUT /api/v1/warranty/claims/:claimId
    UPDATE_CLAIM: (claimId: string) => `${BASE_PATH}/warranty/claims/${claimId}`,
  },

  /**
   * Protection Plans
   */
  PROTECTION_PLANS: {
    // GET /api/v1/protection-plans?homeId=&assetId=
    GET_ALL: `${BASE_PATH}/protection-plans`,
    
    // GET /api/v1/protection-plans/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/protection-plans/${id}`,
    
    // POST /api/v1/protection-plans
    CREATE: `${BASE_PATH}/protection-plans`,
    
    // PUT /api/v1/protection-plans/:id
    UPDATE: (id: string) => `${BASE_PATH}/protection-plans/${id}`,
    
    // DELETE /api/v1/protection-plans/:id
    CANCEL: (id: string) => `${BASE_PATH}/protection-plans/${id}`,
  },

  /**
   * Orders
   */
  ORDERS: {
    // GET /api/v1/orders
    GET_ALL: `${BASE_PATH}/orders`,
    
    // POST /api/v1/orders
    CREATE: `${BASE_PATH}/orders`,
    
    // GET /api/v1/orders/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/orders/${id}`,
    
    // PUT /api/v1/orders/:id
    UPDATE: (id: string) => `${BASE_PATH}/orders/${id}`,
    
    // PUT /api/v1/orders/:id/cancel
    CANCEL: (id: string) => `${BASE_PATH}/orders/${id}/cancel`,
    
    // PATCH /api/v1/orders/:id/link-booking
    LINK_BOOKING: (id: string) => `${BASE_PATH}/orders/${id}/link-booking`,
  },

  /**
   * Payments
   */
  PAYMENTS: {
    // POST /api/v1/payments/intent
    CREATE_INTENT: `${BASE_PATH}/payments/intent`,
    
    // GET /api/v1/payments
    GET_ALL: `${BASE_PATH}/payments`,
    
    // GET /api/v1/payments/:id
    GET_BY_ID: (id: string) => `${BASE_PATH}/payments/${id}`,
    
    // POST /api/v1/payments/webhook - Stripe webhook (raw body)
    WEBHOOK: `${BASE_PATH}/payments/webhook`,
  },

  /**
   * Payment Methods
   */
  PAYMENT_METHODS: {
    // GET /api/v1/payment-methods
    GET_ALL: `${BASE_PATH}/payment-methods`,
    
    // POST /api/v1/payment-methods/setup
    CREATE_SETUP_INTENT: `${BASE_PATH}/payment-methods/setup`,
    
    // POST /api/v1/payment-methods/attach
    ATTACH: `${BASE_PATH}/payment-methods/attach`,
    
    // PATCH /api/v1/payment-methods/:id/default
    SET_DEFAULT: (id: string) => `${BASE_PATH}/payment-methods/${id}/default`,
    
    // DELETE /api/v1/payment-methods/:id
    DELETE: (id: string) => `${BASE_PATH}/payment-methods/${id}`,
  },

  /**
   * Notifications
   */
  NOTIFICATIONS: {
    // GET /api/v1/notifications
    GET_ALL: `${BASE_PATH}/notifications`,
    
    // PUT /api/v1/notifications/:id/read
    MARK_READ: (id: string) => `${BASE_PATH}/notifications/${id}/read`,
    
    // PUT /api/v1/notifications/read-all
    MARK_ALL_READ: `${BASE_PATH}/notifications/read-all`,
    
    // DELETE /api/v1/notifications/:id
    DELETE: (id: string) => `${BASE_PATH}/notifications/${id}`,
  },

  /**
   * Family & Access Management
   */
  FAMILY: {
    // GET /api/v1/family?homeId=
    GET_MEMBERS: `${BASE_PATH}/family`,
    
    // POST /api/v1/family/invite
    INVITE: `${BASE_PATH}/family/invite`,
    
    // GET /api/v1/family/pending-invites?homeId=
    GET_PENDING_INVITES: `${BASE_PATH}/family/pending-invites`,
    
    // DELETE /api/v1/family/invites/:inviteId
    CANCEL_INVITE: (inviteId: string) => `${BASE_PATH}/family/invites/${inviteId}`,
    
    // POST /api/v1/family/resend-invite
    RESEND_INVITE: `${BASE_PATH}/family/resend-invite`,
    
    // GET /api/v1/family/validate-invite?token=
    VALIDATE_INVITE: `${BASE_PATH}/family/validate-invite`,
    
    // POST /api/v1/family/accept-invite
    ACCEPT_INVITE: `${BASE_PATH}/family/accept-invite`,
    
    // POST /api/v1/family/decline-invite
    DECLINE_INVITE: `${BASE_PATH}/family/decline-invite`,
    
    // GET /api/v1/family/co-owners?homeId=
    GET_CO_OWNERS: `${BASE_PATH}/family/co-owners`,
    
    // DELETE /api/v1/family/co-owners/:homeOwnerId
    REMOVE_CO_OWNER: (homeOwnerId: string) => `${BASE_PATH}/family/co-owners/${homeOwnerId}`,
    
    // POST /api/v1/family/members/:userId/grant-home
    GRANT_HOME_ACCESS: (userId: string) => `${BASE_PATH}/family/members/${userId}/grant-home`,
  },

  /**
   * Network Discovery
   */
  DISCOVERY: {
    // POST /api/v1/discovery/enrich
    ENRICH_DEVICES: `${BASE_PATH}/discovery/enrich`,
    
    // POST /api/v1/discovery/register
    REGISTER_DEVICE: `${BASE_PATH}/discovery/register`,
    
    // POST /api/v1/discovery/register-scan
    REGISTER_SCAN: `${BASE_PATH}/discovery/register-scan`,
    
    // GET /api/v1/discovery/history?limit=&offset=
    GET_HISTORY: `${BASE_PATH}/discovery/history`,
  },

  /**
   * AI & Intelligence Features
   */
  AI: {
    // General AI endpoints
    // POST /api/v1/ai/diagnose
    DIAGNOSE_ISSUE: `${BASE_PATH}/ai/diagnose`,
    
    // POST /api/v1/ai/maintenance-tip
    GET_MAINTENANCE_TIP: `${BASE_PATH}/ai/maintenance-tip`,
    
    // POST /api/v1/ai/chat
    CHAT: `${BASE_PATH}/ai/chat`,
    
    // Label & Nameplate AI
    // POST /api/v1/ai/get-brands
    GET_BRANDS: `${BASE_PATH}/ai/get-brands`,
    
    // POST /api/v1/ai/get-subcategories
    GET_SUBCATEGORIES: `${BASE_PATH}/ai/get-subcategories`,
    
    // POST /api/v1/ai/label-locations
    GET_LABEL_LOCATIONS: `${BASE_PATH}/ai/label-locations`,
    
    // POST /api/v1/ai/nameplate-guidance
    GET_NAMEPLATE_GUIDANCE: `${BASE_PATH}/ai/nameplate-guidance`,
    
    // POST /api/v1/ai/analyze-label
    ANALYZE_LABEL: `${BASE_PATH}/ai/analyze-label`,
    
    // POST /api/v1/ai/generate-wireframe
    GENERATE_WIREFRAME: `${BASE_PATH}/ai/generate-wireframe`,
    
    // Barcode & Product Lookup
    // POST /api/v1/ai/barcode-lookup
    BARCODE_LOOKUP: `${BASE_PATH}/ai/barcode-lookup`,
    
    // POST /api/v1/ai/identify-product
    IDENTIFY_PRODUCT: `${BASE_PATH}/ai/identify-product`,
    
    // POST /api/v1/ai/resolve-product-image
    RESOLVE_PRODUCT_IMAGE: `${BASE_PATH}/ai/resolve-product-image`,
    
    // Assessment & Troubleshooting
    // POST /api/v1/ai/generate-issue-options
    GENERATE_ISSUE_OPTIONS: `${BASE_PATH}/ai/generate-issue-options`,
    
    // POST /api/v1/ai/analyze-issue
    ANALYZE_ISSUE: `${BASE_PATH}/ai/analyze-issue`,
    
    // POST /api/v1/ai/generate-diy-troubleshooting
    GENERATE_DIY_TROUBLESHOOTING: `${BASE_PATH}/ai/generate-diy-troubleshooting`,
    
    // POST /api/v1/ai/generate-diy-steps
    GENERATE_DIY_STEPS: `${BASE_PATH}/ai/generate-diy-steps`,
    
    // POST /api/v1/ai/generate-parts
    GENERATE_PARTS: `${BASE_PATH}/ai/generate-parts`,
    
    // POST /api/v1/ai/generate-maintenance-parts
    GENERATE_MAINTENANCE_PARTS: `${BASE_PATH}/ai/generate-maintenance-parts`,
    
    // POST /api/v1/ai/generate-maintenance-guide
    GENERATE_MAINTENANCE_GUIDE: `${BASE_PATH}/ai/generate-maintenance-guide`,

    // POST /api/v1/ai/full-diagnosis (parallel: analyze-issue + DIY + parts in one call)
    FULL_DIAGNOSIS: `${BASE_PATH}/ai/full-diagnosis`,
    
    // POST /api/v1/ai/asset-insights
    ASSET_INSIGHTS: `${BASE_PATH}/ai/asset-insights`,
    
    // Checkout
    // POST /api/v1/ai/checkout
    CHECKOUT: `${BASE_PATH}/ai/checkout`,
  },

  /**
   * BrandSmart Import
   */
  BRANDSMART: {
    // POST /api/v1/brandsmart/lookup
    LOOKUP: `${BASE_PATH}/brandsmart/lookup`,

    // POST /api/v1/brandsmart/import
    IMPORT: `${BASE_PATH}/brandsmart/import`,

    // POST /api/v1/brandsmart/send-otp
    SEND_OTP: `${BASE_PATH}/brandsmart/send-otp`,

    // POST /api/v1/brandsmart/verify-otp
    VERIFY_OTP: `${BASE_PATH}/brandsmart/verify-otp`,

    // POST /api/v1/brandsmart/auto-lookup (auto-match logged-in user's purchases)
    AUTO_LOOKUP: `${BASE_PATH}/brandsmart/auto-lookup`,

    // GET /api/v1/brandsmart/asset-images?ids=
    ASSET_IMAGES: `${BASE_PATH}/brandsmart/asset-images`,
  },

  /**
   * Public & Utility Endpoints
   */
  PUBLIC: {
    // GET /health
    HEALTH: '/health',
    
    // GET /api/v1/invite/redirect
    INVITE_REDIRECT: `${BASE_PATH}/invite/redirect`,
    
    // GET /api/v1/invite/preview
    INVITE_PREVIEW: `${BASE_PATH}/invite/preview`,
    
    // GET /api/v1/api-health (requires auth, dev only)
    API_HEALTH: `${BASE_PATH}/api-health`,
  },

  /**
   * Static Resources
   */
  STATIC: {
    // GET /uploads/:filename
    UPLOADS: (filename: string) => `/uploads/${filename}`,
    
    // GET /images/:filename
    IMAGES: (filename: string) => `/images/${filename}`,
  },

  /**
   * Business Rules — server-side single source of truth for tax,
   * financing, trade-in, service pricing, energy savings.
   * All hardcoded client formulas have been removed.
   */
  BUSINESS_RULES: {
    // GET /api/v1/business-rules
    GET: `${BASE_PATH}/business-rules`,
    // POST /api/v1/business-rules/financing
    FINANCING: `${BASE_PATH}/business-rules/financing`,
    // POST /api/v1/business-rules/trade-in
    TRADE_IN: `${BASE_PATH}/business-rules/trade-in`,
    // POST /api/v1/business-rules/service-price
    SERVICE_PRICE: `${BASE_PATH}/business-rules/service-price`,
    // POST /api/v1/business-rules/energy-savings
    ENERGY_SAVINGS: `${BASE_PATH}/business-rules/energy-savings`,
  },
} as const;

/**
 * Helper function to build query string from params
 */
export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

/**
 * Helper to build endpoint URL with query params
 * 
 * Usage:
 * buildEndpointUrl(API_ENDPOINTS.ASSETS.GET_ALL, { homeId: '123' })
 * // Returns: '/v1/assets?homeId=123'
 */
export function buildEndpointUrl(
  endpoint: string,
  params?: Record<string, unknown>
): string {
  if (!params) return endpoint;
  return `${endpoint}${buildQueryString(params)}`;
}

/**
 * Type-safe API endpoint helper
 * 
 * Usage:
 * const endpoint = getEndpoint('ASSETS', 'GET_BY_ID', 'asset-123');
 */
export function getEndpoint(
  domain: keyof typeof API_ENDPOINTS,
  method: string,
  ...args: unknown[]
): string {
  const domainEndpoints = API_ENDPOINTS[domain];
  const endpoint = (domainEndpoints as Record<string, unknown>)[method];
  
  if (typeof endpoint === 'function') {
    return endpoint(...args);
  }
  
  return endpoint as string;
}

export default API_ENDPOINTS;
